import { makeStyles } from '@material-ui/styles'
import { ephemeralStorageDoc } from 'app/docLinks'
import ExternalLink from 'core/components/ExternalLink'
import UsageWidget from 'core/components/widgets/UsageWidget'
import { METRICS_API_KEYS } from 'core/components/widgets/useTrendData'
import Divider from 'core/elements/Divider'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import { useMemo } from 'react'
import { getHostAllocationTotals, getHostUsageTotals } from './helpers'

interface Props {
  hosts: any[]
  hypervisors?: any[]
  storagePools?: any[]
  perHostMetrics?: boolean
  showAllocation?: boolean
  showOnlyForSelectedHosts?: boolean
  showEphemeralAndBlockStorage?: boolean
}

const useStyles = makeStyles<Theme, { isPersistentStorageRole: boolean }>((theme) => ({
  container: {
    display: 'grid',
    gap: 8,
  },
  hostCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
    gap: theme.spacing(1.5),
    margin: theme.spacing(2, 0),
  },
  sectionHeader: {
    backgroundColor: theme.components.card.background,
    borderRadius: '4px 4px 2px 2px',
    border: `1px solid ${theme.components.card.border}`,
    padding: '16px 24px',
  },
  tooltip: {
    marginLeft: 5,
  },
  horizontalDivider: {
    margin: theme.spacing(1, 0),
  },
  tooltipContent: {
    maxWidth: '300px',
    padding: theme.spacing(1),
  },
  mt1: {
    marginTop: theme.spacing(1),
  },
}))

export default function HostsListPageHeader({
  hosts,
  hypervisors = [],
  storagePools = [],
  perHostMetrics = false,
  showAllocation = false,
  showOnlyForSelectedHosts = false,
  showEphemeralAndBlockStorage = false,
}: Props) {
  const isPersistentStorageRole = hosts?.some((host) =>
    host?.displayRoles?.includes('Persistent Storage'),
  )
  const classes = useStyles({ isPersistentStorageRole })

  const ephemeralStorageTooltipContent = (
    <div className={classes.tooltipContent}>
      Shows the current % utilization of Ephemeral Storage across all hypervisor hosts in this
      region. <ExternalLink url={ephemeralStorageDoc}>What is ephemeral storage</ExternalLink>
    </div>
  )

  const totals = getHostUsageTotals(hosts)

  const allocation = useMemo(() => {
    if (!showAllocation) {
      return {
        compute: {
          current: 0,
          max: 0,
          percent: 0,
        },
        memory: {
          current: 0,
          max: 0,
          percent: 0,
        },
        ephemeralStorage: {
          current: 0,
          max: 0,
          percent: 0,
        },
        blockStorage: {
          current: 0,
          max: 0,
          percent: 0,
        },
      }
    }
    return getHostAllocationTotals({ hosts, hypervisors, storagePools })
  }, [hosts, hypervisors, storagePools, showAllocation])

  const hostIds = useMemo(() => hosts.map((host) => host.id), [hosts])

  return (
    <div className={classes.container}>
      {showAllocation && (
        <>
          <div className={classes.sectionHeader}>
            <Text variant="subtitle2">Allocation</Text>
            <Text variant="body2">
              Shows the total virtualized resources allocated across all the virtual machines
              provisioned on {perHostMetrics ? <b>{hosts?.[0]?.name}</b> : 'the hosts'}.
            </Text>
          </div>
          <div className={classes.hostCards}>
            <UsageWidget
              icon="microchip"
              title="CPU"
              stats={allocation?.compute}
              units="vCPUs"
              primary={`out of ${allocation?.compute?.max} vCPUs`}
              allocation
            />
            <UsageWidget
              icon="memory"
              title="Memory"
              stats={allocation?.memory}
              units="GiB"
              primary={`out of ${allocation?.memory?.max?.toFixed(1)} GiB`}
              allocation
            />
            {showEphemeralAndBlockStorage && (
              <>
                <UsageWidget
                  icon="sim-card"
                  title="Ephemeral Storage"
                  stats={allocation?.ephemeralStorage}
                  units="GB"
                  primary={`out of ${allocation?.ephemeralStorage?.max?.toFixed(1)} GB`}
                  allocation
                />
                {isPersistentStorageRole && (
                  <UsageWidget
                    icon="sim-card"
                    title="Block Storage"
                    stats={allocation?.blockStorage}
                    units="GB"
                    primary={`out of ${allocation?.blockStorage?.max?.toFixed(1)} GB`}
                    allocation
                  />
                )}
              </>
            )}
          </div>
          <div className={classes.sectionHeader}>
            <Text variant="subtitle2">Usage</Text>
            <Text variant="body2">
              Shows total used resources{' '}
              {perHostMetrics ? (
                <>
                  on <b>{hosts?.[0]?.name}</b>
                </>
              ) : (
                'across all hosts'
              )}
              .
            </Text>
          </div>
        </>
      )}
      {/* Compute Section */}
      <Text variant="subtitle2" className={classes.mt1}>
        Compute
      </Text>
      <Divider className={classes.horizontalDivider} />
      <div className={classes.hostCards}>
        <UsageWidget
          icon="microchip"
          title="CPU"
          stats={totals.compute}
          units="GHz"
          primary={`out of ${totals.compute?.max?.toFixed(1)} GHz`}
          metricKey={
            perHostMetrics
              ? METRICS_API_KEYS.PER_HOST_CPU_USAGE
              : showOnlyForSelectedHosts
              ? METRICS_API_KEYS.SELECTED_HOST_CPU_USAGE
              : METRICS_API_KEYS.ALL_HOSTS_CPU_USAGE
          }
          resourceId={hosts[0]?.id}
          hostIds={showOnlyForSelectedHosts ? hostIds : []}
          dialTooltipMsg="Shows the current % utilization of total CPU across all hypervisor hosts in this region."
          chartTooltipMsg="Shows a graph of CPU utilization over the selected period of time, across all hypervisor hosts in this region."
        />
        <UsageWidget
          icon="memory"
          title="Memory"
          stats={totals.memory}
          units="GiB"
          primary={`out of ${totals.memory?.max?.toFixed(1)} GiB`}
          metricKey={
            perHostMetrics
              ? METRICS_API_KEYS.PER_HOST_MEMORY_USAGE
              : showOnlyForSelectedHosts
              ? METRICS_API_KEYS.SELECTED_HOST_MEMORY_USAGE
              : METRICS_API_KEYS.ALL_HOSTS_MEMORY_USAGE
          }
          resourceId={hosts[0]?.id}
          hostIds={showOnlyForSelectedHosts ? hostIds : []}
          dialTooltipMsg="Shows the current % utilization of total Memory across all hypervisor hosts in this region."
          chartTooltipMsg="Shows a graph of Memory utilization over the selected period of time, across all hypervisor hosts in this region."
        />
      </div>
      {/*
      lets keep showEphemeralAndBlockStorage set to false until we have the correct stats available via API
      check https://platform9.atlassian.net/browse/PCD-5268 for more details
      */}
      {/* Storage Section */}
      {showEphemeralAndBlockStorage && (
        <>
          <div className="flex">
            <Text variant="subtitle2">Storage</Text>
          </div>
          <Divider className={classes.horizontalDivider} />
          <div className={classes.hostCards}>
            <UsageWidget
              icon="sim-card"
              title="Ephemeral Storage"
              stats={totals.ephemeralDisk}
              units="GiB"
              primary={`out of ${totals.ephemeralDisk?.max?.toFixed(1)} GiB`}
              showEmptyGraphMessage
              resourceId={hosts[0]?.id}
              hostIds={showOnlyForSelectedHosts ? hostIds : []}
              dialTooltipMsg={() => ephemeralStorageTooltipContent}
            />
            {isPersistentStorageRole ? (
              <UsageWidget
                icon="sim-card"
                title="Block Storage"
                stats={totals.blockStorageDisk}
                units="GiB"
                primary={`out of ${totals.blockStorageDisk?.max?.toFixed(1)} GiB`}
                resourceId={hosts[0]?.id}
                metricKey={
                  perHostMetrics
                    ? METRICS_API_KEYS.PER_HOST_DISK_USAGE
                    : showOnlyForSelectedHosts
                    ? METRICS_API_KEYS.SELECTED_HOST_DISK_USAGE
                    : METRICS_API_KEYS.ALL_HOSTS_DISK_USAGE
                }
                hostIds={showOnlyForSelectedHosts ? hostIds : []}
              />
            ) : (
              // Keep this div to maintain the grid layout
              <div />
            )}
          </div>
        </>
      )}
    </div>
  )
}
